import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { CallToolResult } from "@modelcontextprotocol/sdk/types.js";
import { ZodError } from "zod/v3";

import { PrimeUiProjectConfigError } from "./lib/project-link-config.js";
import {
  componentCandidatesGetValidationSchema,
  componentPropsValidateValidationSchema,
  initialInstructions,
  projectPageCreateValidationSchema,
  projectPageVariantCreateValidationSchema,
  toolComponentCandidatesGet,
  toolComponentPropsValidate,
  toolProjectDescriptionGet,
  toolProjectDescriptionUpsert,
  toolProjectPagesList,
  toolProjectPageGet,
  toolProjectPageCreate,
  toolProjectPageVariantsList,
  toolProjectPageVariantGet,
  toolProjectPageVariantCreate,
  toolProjectPageActiveVariantSet,
  toolProjectIssueReportSubmit,
  toolSearchOverviewGet,
  toolSearchEnvironmentGet,
  toolSearchEnvironmentCreate,
  toolSearchEnvironmentUpdate,
  toolSearchEnvironmentDelete,
  toolSearchEnvironmentSync,
  toolClearTemp,
  toolCopyComponent,
  toolCopyExportable,
  toolCopyRegistryComponent,
  toolInspectPage,
  toolCopyPage,
  toolCreateComponentExport,
  toolCreateExport,
  toolDownloadComponentExport,
  toolDownloadExport,
  toolGetProjectInfo,
  toolListExports,
} from "./instructions.js";
import { packageMetadata } from "./package-metadata.js";
import type { ProjectSyncSource } from "./sources/project-sync-source.js";
import type {
  PrimeUiComponentCandidatesResult,
  PrimeUiComponentPropsValidateResult,
  PrimeUiCopyComponentResult,
  PrimeUiCopyExportableResult,
  PrimeUiCreateComponentExportResult,
  PrimeUiCopyRegistryComponentResult,
  PrimeUiCreateExportResult,
  PrimeUiIssueReportSubmitResult,
  PrimeUiManagedSearchEnvironmentResult,
  PrimeUiManagedSearchOverviewResult,
  PrimeUiManagedSearchRuntimeDeleteResult,
  PrimeUiManagedSearchRuntimeMutationResult,
  PrimeUiManagedSearchSyncResult,
  PrimeUiCopyPageResult,
  PrimeUiDownloadResult,
  PrimeUiExport,
  PrimeUiInspectPageResult,
  PrimeUiProjectApiPagesResult,
  PrimeUiProjectApiVariantResult,
  PrimeUiProjectApiVariantSummaryResult,
  PrimeUiProjectApiVariantsResult,
  PrimeUiProjectDescriptionResult,
  PrimeUiProjectInfo,
} from "./types.js";

const ZERO_ARG_PROJECT_ROOT_TOOLS: ReadonlySet<string> = new Set([
  "get_project_info",
  "project_description_get",
  "project_pages_list",
  "list_exports",
  "create_export",
  "clear_temp",
  "search_overview_get",
] as const);

type ToolValidationShim = {
  inputSchema?: unknown;
};

type McpServerValidationShim = {
  validateToolInput: (
    tool: ToolValidationShim,
    args: unknown,
    toolName: string,
  ) => Promise<unknown>;
};

function serializeToolData(data: unknown): string {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return JSON.stringify(
      {
        error: "Unable to serialize structuredContent for text output.",
      },
      null,
      2,
    );
  }
}

function okResult<T>(title: string, data: T): CallToolResult {
  const structuredContent = data as Record<string, unknown>;
  const serializedData = serializeToolData(structuredContent);
  return {
    content: [
      { type: "text", text: `${title} completed\n\n${serializedData}` },
    ],
    structuredContent,
  };
}

function errorResult(error: unknown): CallToolResult {
  if (error instanceof PrimeUiProjectConfigError) {
    const text = error.hint
      ? `primeui mcp error [${error.code}]: ${error.message}\nHint: ${error.hint}`
      : `primeui mcp error [${error.code}]: ${error.message}`;

    return {
      isError: true,
      content: [{ type: "text", text }],
    };
  }

  if (error instanceof ZodError) {
    const details = error.issues
      .map((issue) => `${issue.path.join(".") || "<root>"}: ${issue.message}`)
      .join("; ");

    return {
      isError: true,
      content: [
        {
          type: "text",
          text: `primeui mcp error [INVALID_INPUT]: ${details}`,
        },
      ],
    };
  }

  const message = error instanceof Error ? error.message : String(error);
  return {
    isError: true,
    content: [
      { type: "text", text: `primeui mcp error [UNKNOWN_ERROR]: ${message}` },
    ],
  };
}

export function createPrimeUiMcpServer(source: ProjectSyncSource): McpServer {
  const server = new McpServer(
    {
      name: "primeui-mcp-server",
      version: packageMetadata.version,
    },
    {
      instructions: initialInstructions,
    },
  );
  const serverWithValidationShim = server as unknown as McpServerValidationShim;
  const validateToolInput =
    serverWithValidationShim.validateToolInput.bind(server);

  serverWithValidationShim.validateToolInput = async (
    tool: ToolValidationShim,
    args: unknown,
    toolName: string,
  ): Promise<unknown> => {
    if (args === undefined && ZERO_ARG_PROJECT_ROOT_TOOLS.has(toolName)) {
      return validateToolInput(tool, {}, toolName);
    }

    return validateToolInput(tool, args, toolName);
  };

  server.registerTool(
    "get_project_info",
    toolGetProjectInfo,
    async (args): Promise<CallToolResult> => {
      const { projectRoot } = args ?? {};
      try {
        const info: PrimeUiProjectInfo = await source.getProjectInfo({
          projectRoot,
        });
        return okResult("get_project_info", info);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_description_get",
    toolProjectDescriptionGet,
    async (args): Promise<CallToolResult> => {
      const { projectRoot } = args ?? {};
      try {
        const result: PrimeUiProjectDescriptionResult =
          await source.getProjectDescription({
            projectRoot,
          });
        return okResult("project_description_get", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_description_upsert",
    toolProjectDescriptionUpsert,
    async ({ projectDescription, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiProjectDescriptionResult =
          await source.upsertProjectDescription(projectDescription, {
            projectRoot,
          });
        return okResult("project_description_upsert", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_pages_list",
    toolProjectPagesList,
    async (args): Promise<CallToolResult> => {
      const { projectRoot } = args ?? {};
      try {
        const result: PrimeUiProjectApiPagesResult =
          await source.listProjectPages({
            projectRoot,
          });
        return okResult("project_pages_list", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_page_get",
    toolProjectPageGet,
    async ({ pageId, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiProjectApiPagesResult =
          await source.getProjectPage(pageId, { projectRoot });
        return okResult("project_page_get", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_page_create",
    toolProjectPageCreate,
    async (args): Promise<CallToolResult> => {
      try {
        const { projectRoot, ...input } =
          projectPageCreateValidationSchema.parse(args);
        const result: PrimeUiProjectApiPagesResult =
          await source.createProjectPage(input, { projectRoot });
        return okResult("project_page_create", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_page_variants_list",
    toolProjectPageVariantsList,
    async ({ pageId, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiProjectApiVariantsResult =
          await source.listProjectPageVariants(pageId, {
            projectRoot,
          });
        return okResult("project_page_variants_list", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_page_variant_get",
    toolProjectPageVariantGet,
    async ({ pageId, variantId, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiProjectApiVariantResult =
          await source.getProjectPageVariant(pageId, variantId, {
            projectRoot,
          });
        return okResult("project_page_variant_get", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_page_variant_create",
    toolProjectPageVariantCreate,
    async (args): Promise<CallToolResult> => {
      try {
        const { pageId, projectRoot, ...input } =
          projectPageVariantCreateValidationSchema.parse(args);
        const result: PrimeUiProjectApiVariantSummaryResult =
          await source.createProjectPageVariant(pageId, input, {
            projectRoot,
          });
        return okResult("project_page_variant_create", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_page_active_variant_set",
    toolProjectPageActiveVariantSet,
    async ({ pageId, variantId, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiProjectApiPagesResult =
          await source.setProjectPageActiveVariant(pageId, variantId, {
            projectRoot,
          });
        return okResult("project_page_active_variant_set", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "project_issue_report_submit",
    toolProjectIssueReportSubmit,
    async ({ reportText, tags, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiIssueReportSubmitResult =
          await source.submitIssueReport(
            {
              reportText,
              tags,
            },
            { projectRoot },
          );
        return okResult("project_issue_report_submit", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "inspect_page",
    toolInspectPage,
    async ({ pageSlug, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiInspectPageResult = await source.inspectPage(
          pageSlug,
          { projectRoot },
        );
        return okResult("inspect_page", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "component_candidates_get",
    toolComponentCandidatesGet,
    async (args): Promise<CallToolResult> => {
      try {
        const { projectRoot, ...input } =
          componentCandidatesGetValidationSchema.parse(args);
        const result: PrimeUiComponentCandidatesResult =
          await source.getComponentCandidates(input, { projectRoot });
        return okResult("component_candidates_get", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "component_props_validate",
    toolComponentPropsValidate,
    async (args): Promise<CallToolResult> => {
      try {
        const { projectRoot, ...input } =
          componentPropsValidateValidationSchema.parse(args);
        const result: PrimeUiComponentPropsValidateResult =
          await source.validateComponentProps(input, { projectRoot });
        return okResult("component_props_validate", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "list_exports",
    toolListExports,
    async (args): Promise<CallToolResult> => {
      const { projectRoot } = args ?? {};
      try {
        const exportsList: PrimeUiExport[] = await source.listExports({
          projectRoot,
        });
        return okResult("list_exports", { exports: exportsList });
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "create_export",
    toolCreateExport,
    async (args): Promise<CallToolResult> => {
      const { projectRoot } = args ?? {};
      try {
        const result: PrimeUiCreateExportResult = await source.createExport({
          projectRoot,
        });
        return okResult("create_export", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "create_component_export",
    toolCreateComponentExport,
    async ({
      componentId,
      projectRoot,
      propsValidationId,
    }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiCreateComponentExportResult =
          await source.createComponentExport(
            componentId,
            { propsValidationId },
            { projectRoot },
          );
        return okResult("create_component_export", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "download_export",
    toolDownloadExport,
    async ({ id, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiDownloadResult = await source.downloadExportById(
          id,
          { projectRoot },
        );
        return okResult("download_export", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "download_component_export",
    toolDownloadComponentExport,
    async ({ id, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiDownloadResult =
          await source.downloadComponentExportById(id, {
            projectRoot,
          });
        return okResult("download_component_export", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "clear_temp",
    toolClearTemp,
    async (args): Promise<CallToolResult> => {
      const { projectRoot } = args ?? {};
      try {
        await source.clearTemp({ projectRoot });
        return okResult("clear_temp", { success: true });
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "copy_page",
    toolCopyPage,
    async ({ originPageSlug, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiCopyPageResult = await source.copyPage(
          originPageSlug,
          { projectRoot },
        );
        return okResult("copy_page", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "copy_component",
    toolCopyComponent,
    async ({
      originPageSlug,
      blockId,
      componentId,
      projectRoot,
    }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiCopyComponentResult = await source.copyComponent(
          originPageSlug,
          blockId,
          componentId,
          {
            projectRoot,
          },
        );
        return okResult("copy_component", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "copy_registry_component",
    toolCopyRegistryComponent,
    async ({ componentId, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiCopyRegistryComponentResult =
          await source.copyRegistryComponent(componentId, {
            projectRoot,
          });
        return okResult("copy_registry_component", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "copy_exportable",
    toolCopyExportable,
    async ({ exportableKey, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiCopyExportableResult = await source.copyExportable(
          exportableKey,
          {
            projectRoot,
          },
        );
        return okResult("copy_exportable", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "search_overview_get",
    toolSearchOverviewGet,
    async (args): Promise<CallToolResult> => {
      const { projectRoot } = args ?? {};
      try {
        const result: PrimeUiManagedSearchOverviewResult =
          await source.getSearchOverview({ projectRoot });
        return okResult("search_overview_get", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "search_environment_get",
    toolSearchEnvironmentGet,
    async ({ environmentSlug, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiManagedSearchEnvironmentResult =
          await source.getSearchEnvironment(environmentSlug, { projectRoot });
        return okResult("search_environment_get", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "search_environment_create",
    toolSearchEnvironmentCreate,
    async ({ name, projectRoot, slug }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiManagedSearchRuntimeMutationResult =
          await source.createSearchEnvironment(
            {
              name,
              slug,
            },
            { projectRoot },
          );
        return okResult("search_environment_create", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "search_environment_update",
    toolSearchEnvironmentUpdate,
    async ({
      environmentSlug,
      name,
      projectRoot,
      status,
    }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiManagedSearchRuntimeMutationResult =
          await source.updateSearchEnvironment(
            {
              environmentSlug,
              ...(name === undefined ? {} : { name }),
              ...(status === undefined ? {} : { status }),
            },
            { projectRoot },
          );
        return okResult("search_environment_update", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "search_environment_delete",
    toolSearchEnvironmentDelete,
    async ({ environmentSlug, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiManagedSearchRuntimeDeleteResult =
          await source.deleteSearchEnvironment(environmentSlug, {
            projectRoot,
          });
        return okResult("search_environment_delete", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  server.registerTool(
    "search_environment_sync",
    toolSearchEnvironmentSync,
    async ({ environmentSlug, projectRoot }): Promise<CallToolResult> => {
      try {
        const result: PrimeUiManagedSearchSyncResult =
          await source.syncSearchEnvironment(environmentSlug, { projectRoot });
        return okResult("search_environment_sync", result);
      } catch (error) {
        return errorResult(error);
      }
    },
  );

  return server;
}
